import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { statisticsKeys } from './statistics.queries';
import { useTranslation } from '../../../i18n';
import type { Note, NoteCreateRequest, NoteUpdateRequest } from '../../../types/api';

// STD-015: write/imperative actions for the Statistics domain. apiService is
// imported here (a *.mutations.ts file) per STD-016. Errors are toasted by the
// global MutationCache handler; meta.errorMessage is the fallback.

// --- Optimistic notes-cache helpers (shared by every note mutation) ---------
//
// The notes query is keyed per (serial, filters), so one serial can have several
// cached entries (e.g. ProfileTab's all-notes view vs NotesListView's filtered
// list). The old window-event hook mutated the local state of *every* mounted
// useNotesHistory instance; to match that, these helpers apply the optimistic
// change to all cached notes queries for the serial via setQueriesData, and
// snapshot them all so onError can roll every one back.

interface NotesOptimisticContext {
  previous: Array<[readonly unknown[], Note[] | undefined]>;
}

// Cancel in-flight notes refetches for the serial, snapshot all cached notes
// queries, then apply `updater` to each. Returns the snapshot for rollback.
async function optimisticNotesUpdate(
  queryClient: QueryClient,
  droneSerial: string,
  updater: (notes: Note[]) => Note[]
): Promise<NotesOptimisticContext> {
  const queryKey = statisticsKeys.notes(droneSerial);
  await queryClient.cancelQueries({ queryKey });
  const previous = queryClient.getQueriesData<Note[]>({ queryKey });
  queryClient.setQueriesData<Note[]>({ queryKey }, (old) => (old ? updater(old) : old));
  return { previous };
}

// Restore the pre-mutation snapshot when a note mutation fails.
function rollbackNotes(queryClient: QueryClient, context: NotesOptimisticContext | undefined) {
  if (!context) return;
  for (const [key, data] of context.previous) {
    queryClient.setQueryData(key, data);
  }
}

// Replace the note matching `noteId` in a list, applying `patch` to it.
function patchNoteById(notes: Note[], noteId: string, patch: Partial<Note>): Note[] {
  return notes.map((note) => (note.id === noteId ? { ...note, ...patch } : note));
}

// Flight weather + metadata mutations moved to the shared
// `components/flight-details/flightDetails.mutations.ts` module (STD-013).

// Note write actions. Each does an optimistic cache update in onMutate (so the
// notes list/timeline change instantly, as the old window-event hook did),
// rolls back in onError, and invalidates the serial-level notes prefix in
// onSettled so the optimistic state reconciles with the server. This replaces
// the former hand-rolled `window` CustomEvent ('note-created'/'-updated'/
// '-deleted') pub/sub between useNoteOperations and useNotesHistory.

// Create a new note for a drone. Optimistically prepends a provisional note
// (temp id) so it appears immediately; onSettled refetches to swap in the real
// server row (with its real id/created_at/author).
export function useCreateNoteMutation(droneSerial: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    // Dialogs surface their own error UI (inline Alert + showError), so opt out
    // of the global toast to avoid a duplicate. errorMessage stays as the fallback.
    meta: { errorMessage: t('notes.failedToCreate'), skipGlobalErrorToast: true },
    mutationFn: async (noteData: NoteCreateRequest): Promise<Note> => {
      return apiService.createNote(droneSerial, noteData);
    },
    onMutate: async (noteData: NoteCreateRequest) => {
      const now = new Date().toISOString();
      // Provisional row for instant display. Fields not surfaced by the notes
      // list/timeline are filled with safe placeholders; the onSettled refetch
      // replaces this with the authoritative server row.
      const optimisticNote: Note = {
        id: `optimistic-${now}-${Math.random().toString(36).slice(2)}`,
        drone_serial: droneSerial,
        author_id: '',
        note_type: noteData.note_type ?? 'general',
        priority: noteData.priority ?? 'information',
        subject: noteData.subject,
        content: noteData.content,
        attachments: noteData.attachments ?? [],
        created_at: now,
        updated_at: now,
        organization_id: '',
        date_of_action: noteData.date_of_action,
      };
      return optimisticNotesUpdate(queryClient, droneSerial, (notes) => [optimisticNote, ...notes]);
    },
    onError: (_err, _vars, context) => {
      rollbackNotes(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: statisticsKeys.notes(droneSerial) });
    },
  });
}

// Update a note's subject/content/type/priority.
export function useUpdateNoteMutation(droneSerial: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('notes.failedToUpdate'), skipGlobalErrorToast: true },
    mutationFn: async ({
      noteId,
      updates,
    }: {
      noteId: string;
      updates: NoteUpdateRequest;
    }): Promise<Note> => {
      return apiService.updateNote(droneSerial, noteId, updates);
    },
    onMutate: async ({ noteId, updates }) => {
      // `tags` lives on NoteUpdateRequest but not on Note, so drop it from the
      // optimistic patch.
      const noteFields = { ...updates };
      delete noteFields.tags;
      return optimisticNotesUpdate(queryClient, droneSerial, (notes) =>
        patchNoteById(notes, noteId, {
          ...noteFields,
          updated_at: new Date().toISOString(),
        })
      );
    },
    onError: (_err, _vars, context) => {
      rollbackNotes(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: statisticsKeys.notes(droneSerial) });
    },
  });
}

// Delete a note.
export function useDeleteNoteMutation(droneSerial: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('notes.failedToDelete'), skipGlobalErrorToast: true },
    mutationFn: async (noteId: string): Promise<void> => {
      await apiService.deleteNote(droneSerial, noteId);
    },
    onMutate: async (noteId: string) => {
      return optimisticNotesUpdate(queryClient, droneSerial, (notes) =>
        notes.filter((note) => note.id !== noteId)
      );
    },
    onError: (_err, _vars, context) => {
      rollbackNotes(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: statisticsKeys.notes(droneSerial) });
    },
  });
}

// Mark a note resolved, with an optional resolution note.
export function useResolveNoteMutation(droneSerial: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('notes.failedToResolve'), skipGlobalErrorToast: true },
    mutationFn: async ({
      noteId,
      resolutionNotes,
    }: {
      noteId: string;
      resolutionNotes?: string;
    }): Promise<Note> => {
      return apiService.resolveNote(droneSerial, noteId, resolutionNotes);
    },
    onMutate: async ({ noteId, resolutionNotes }) => {
      return optimisticNotesUpdate(queryClient, droneSerial, (notes) =>
        patchNoteById(notes, noteId, {
          resolved_at: new Date().toISOString(),
          resolution_notes: resolutionNotes,
        })
      );
    },
    onError: (_err, _vars, context) => {
      rollbackNotes(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: statisticsKeys.notes(droneSerial) });
    },
  });
}

// Mark a resolved note unresolved.
export function useUnresolveNoteMutation(droneSerial: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('notes.failedToUnresolve'), skipGlobalErrorToast: true },
    mutationFn: async (noteId: string): Promise<Note> => {
      return apiService.unresolveNote(droneSerial, noteId);
    },
    onMutate: async (noteId: string) => {
      return optimisticNotesUpdate(queryClient, droneSerial, (notes) =>
        patchNoteById(notes, noteId, {
          resolved_at: undefined,
          resolved_by: undefined,
          resolution_notes: undefined,
        })
      );
    },
    onError: (_err, _vars, context) => {
      rollbackNotes(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: statisticsKeys.notes(droneSerial) });
    },
  });
}

// Update a note's date_of_action.
export function useUpdateNoteDateMutation(droneSerial: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('notes.failedToUpdate'), skipGlobalErrorToast: true },
    mutationFn: async ({
      noteId,
      dateOfAction,
    }: {
      noteId: string;
      dateOfAction: string;
    }): Promise<Note> => {
      return apiService.updateNoteDate(droneSerial, noteId, dateOfAction);
    },
    onMutate: async ({ noteId, dateOfAction }) => {
      return optimisticNotesUpdate(queryClient, droneSerial, (notes) =>
        patchNoteById(notes, noteId, {
          date_of_action: dateOfAction,
          updated_at: new Date().toISOString(),
        })
      );
    },
    onError: (_err, _vars, context) => {
      rollbackNotes(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: statisticsKeys.notes(droneSerial) });
    },
  });
}

// Update a note's subject and/or content via the lightweight content endpoint.
export function useUpdateNoteContentMutation(droneSerial: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('notes.failedToUpdate'), skipGlobalErrorToast: true },
    mutationFn: async ({
      noteId,
      subject,
      content,
    }: {
      noteId: string;
      subject?: string;
      content?: string;
    }): Promise<Note> => {
      return apiService.updateNoteContent(droneSerial, noteId, subject, content);
    },
    onMutate: async ({ noteId, subject, content }) => {
      const patch: Partial<Note> = { updated_at: new Date().toISOString() };
      if (subject !== undefined) patch.subject = subject;
      if (content !== undefined) patch.content = content;
      return optimisticNotesUpdate(queryClient, droneSerial, (notes) =>
        patchNoteById(notes, noteId, patch)
      );
    },
    onError: (_err, _vars, context) => {
      rollbackNotes(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: statisticsKeys.notes(droneSerial) });
    },
  });
}

// Upload a single attachment file for a note — called in a loop from
// CreateNoteDialog for multi-file uploads.
export function useUploadAttachmentMutation(droneSerial: string) {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('statistics.failedToUploadAttachment') },
    mutationFn: async (file: File) => {
      const result = await apiService.uploadAttachment(file, droneSerial);
      return result as { key: string; name?: string; url: string };
    },
  });
}
